import type { FlattenedThemeType, ThemeData, ThemeType } from '@unifyapps/defs/theme/types';
import { BorderVariants } from '@unifyapps/defs/types/interface';
import _compact from 'lodash/compact';
import _map from 'lodash/map';
import { DEFAULT_COLORS } from '../const';
import {
  DEFAULT_CODE_FONT_FAMILY,
  DEFAULT_FONT_FAMILY,
} from '../views/builder/components/TypographyEditor/FontFamilySelector';

export type ThemeDataBorderRadius = ThemeData['borderRadius'];

/** Single source of truth for theme manager border preset ↔ cssVars `border` (BorderVariants). */
const THEME_BORDER_RADIUS_VARIANT_PAIRS: readonly (readonly [
  ThemeDataBorderRadius,
  BorderVariants,
])[] = [
  ['none', BorderVariants.FullySquare],
  ['sm', BorderVariants.SoftSquare],
  ['md', BorderVariants.Rounded],
  ['lg', BorderVariants.FullyRounded],
];

const BORDER_VARIANT_TO_THEME_BORDER_RADIUS = Object.fromEntries(
  THEME_BORDER_RADIUS_VARIANT_PAIRS.map(([radius, variant]) => [variant, radius]),
) as Record<BorderVariants, ThemeDataBorderRadius>;

const THEME_BORDER_RADIUS_TO_BORDER_VARIANT = Object.fromEntries(
  THEME_BORDER_RADIUS_VARIANT_PAIRS,
) as Record<ThemeDataBorderRadius, BorderVariants>;

/**
 * Maps ThemeData `borderRadius` to the `border` value stored in theme `cssVars`.
 */
export function getBorderVariantFromThemeBorderRadius(
  borderRadius: ThemeDataBorderRadius,
): BorderVariants {
  return THEME_BORDER_RADIUS_TO_BORDER_VARIANT[borderRadius];
}
/**
 * Extracts a string value from cssVars, handling unknown type
 */
export function getCssVar(cssVars: Record<string, unknown> | undefined, key: string): string {
  if (!cssVars) return '';
  const value = cssVars[key];
  return typeof value === 'string' ? value : '';
}

export function getThemePreviewColors(cssVars: Record<string, unknown> | undefined): {
  primary: string;
  secondary: string;
  tertiary: string;
} {
  return {
    primary: getCssVar(cssVars, '--palette-brand-600') || DEFAULT_COLORS.primary,
    secondary: getCssVar(cssVars, '--palette-brand-secondary-600') || DEFAULT_COLORS.secondary,
    tertiary: getCssVar(cssVars, '--palette-gray-300') || DEFAULT_COLORS.tertiary,
  };
}

/**
 * Maps border value from cssVars to borderRadius type
 */
function getBorderRadius(cssVars: Record<string, unknown> | undefined): ThemeDataBorderRadius {
  const border = getCssVar(cssVars, 'border');
  const mapped = BORDER_VARIANT_TO_THEME_BORDER_RADIUS[border as BorderVariants];
  return mapped ?? 'md'; // default fallback
}

/**
 * Transforms API theme response (ThemeType) to UI model (ThemeData)
 */
export function transformDTOToThemeDataItem(theme: ThemeType): ThemeData {
  const { id, properties } = theme;
  const cssVars = properties?.cssVars;

  return {
    id,
    name: properties?.name ?? '',
    // isDefault is computed dynamically via useFetchUAPlatformInterface in ThemeCard
    primaryFontFamily: getCssVar(cssVars, '--fontFamily-text') || DEFAULT_FONT_FAMILY,
    codeFontFamily: getCssVar(cssVars, '--fontFamily-code') || DEFAULT_CODE_FONT_FAMILY,
    borderRadius: getBorderRadius(cssVars),
    colors: {
      ...getThemePreviewColors(cssVars),
      quaternary: getCssVar(cssVars, '--bg-primary') || '#E0E7FF',
    },
    hasLightMode: properties?.defaultScheme === 'both' || properties?.defaultScheme === 'light',
    hasDarkMode: properties?.defaultScheme === 'both' || properties?.defaultScheme === 'dark',
    cssVars: cssVars,
    standard: properties?.standard ?? false,
  };
}

/**
 * Transforms an array of API themes to UI models
 */
export function transformDTOToThemeData(themes: ThemeType[]): ThemeData[] {
  return themes.map(transformDTOToThemeDataItem);
}

export function flattenTheme(theme: ThemeType): FlattenedThemeType {
  return {
    id: theme.id,
    ...theme.properties,
  };
}

export function flattenThemeEntities(themes: (ThemeType | undefined)[]): FlattenedThemeType[] {
  return _map(_compact(themes), flattenTheme);
}
